import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Trash2, Download, Check } from "lucide-react";
import { COLORS } from "../hooks/useAudioRecorder";
import { useApiClient } from "../api/client";
import {
  getInvoice,
  setInvoicePaid,
  deleteInvoice,
} from "../api/invoices";
import { generateInvoicePdf, safeInvoiceFilename } from "../utils/generateInvoicePdf";
import { formatInvoiceDate } from "../utils/dateFormat";
import {
  triggerBlobDownload,
  shareOrDownloadPdf,
  canSharePdfFiles,
  isLikelyMobile,
} from "../utils/triggerDownload";
import PageShell from "../components/PageShell";
import AuthChip from "../components/AuthChip";
import BackLink from "../components/BackLink";
import AppFooter from "../components/AppFooter";

const fmtMoney = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  return `$${v.toLocaleString(undefined, {
    minimumFractionDigits: Number.isInteger(v) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
};

export default function InvoiceDetail() {
  const { invoiceId } = useParams();
  const api = useApiClient();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  // Pre-generated PDF for this detail view (per-detail mount, not eager).
  // See Success.jsx for the same pattern — keeps the tap → navigator.share()
  // path synchronous inside the user-activation gesture frame.
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfFilename, setPdfFilename] = useState(null);
  const [showGenerating, setShowGenerating] = useState(false);
  const [canShare, setCanShare] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setCanShare(canSharePdfFiles());
    setIsMobile(isLikelyMobile());
  }, []);

  useEffect(() => {
    let mounted = true;
    getInvoice(api, invoiceId)
      .then((data) => mounted && setInvoice(data))
      .catch((e) => {
        if (!mounted) return;
        setError(
          e?.response?.status === 404
            ? "Invoice not found."
            : e?.response?.data?.detail || e?.message || String(e),
        );
      });
    return () => {
      mounted = false;
    };
  }, [api, invoiceId]);

  useEffect(() => {
    if (!invoice) return undefined;
    let cancelled = false;
    const generatingTimer = setTimeout(() => {
      if (!cancelled) setShowGenerating(true);
    }, 200);
    generateInvoicePdf({
      invoice: invoice.final_extraction || {},
      profile: invoice.profile_snapshot || null,
      meta: {
        invoice_number: invoice.invoice_number,
        subtotal: invoice.subtotal,
        discount_total: invoice.discount_total,
        tax_amount: invoice.tax_amount,
        tax_label: invoice.tax_label,
        total_due: invoice.total_due,
        total: invoice.total_due,
        balance_due: invoice.balance_due,
        client_name: invoice.customer_name,
        created_at: invoice.created_at,
        service_date: invoice.service_date,
        due_date: invoice.due_date,
      },
    })
      .then((blob) => {
        if (cancelled) return;
        clearTimeout(generatingTimer);
        setPdfBlob(blob);
        setPdfFilename(
          invoice.filename ||
            safeInvoiceFilename(invoice.invoice_number, invoice.customer_name),
        );
        setShowGenerating(false);
      })
      .catch((err) => {
        if (cancelled) return;
        clearTimeout(generatingTimer);
        // eslint-disable-next-line no-console
        console.warn("[Ledger] PDF pre-generation failed; tap will fall back to on-demand render.", err);
        setShowGenerating(false);
      });
    return () => {
      cancelled = true;
      clearTimeout(generatingTimer);
    };
  }, [invoice]);

  const onTogglePaid = async () => {
    if (!invoice || busy) return;
    const next = !invoice.paid;
    setBusy(true);
    try {
      const updated = await setInvoicePaid(api, invoiceId, next);
      setInvoice(updated);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await deleteInvoice(api, invoiceId);
      navigate("/invoices", { replace: true });
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || String(e));
      setBusy(false);
    }
  };

  // Build (or reuse the pre-generated) PDF blob + filename. Regenerates from
  // the stored structured data: final_extraction is the FinalizeRequest
  // payload that drove the original render; profile_snapshot is the profile
  // as it was at finalize time.
  const ensureBlob = async () => {
    if (pdfBlob && pdfFilename) return { blob: pdfBlob, filename: pdfFilename };
    const blob = await generateInvoicePdf({
      invoice: invoice.final_extraction || {},
      profile: invoice.profile_snapshot || null,
      meta: {
        invoice_number: invoice.invoice_number,
        subtotal: invoice.subtotal,
        discount_total: invoice.discount_total,
        tax_amount: invoice.tax_amount,
        tax_label: invoice.tax_label,
        total_due: invoice.total_due,
        total: invoice.total_due,
        balance_due: invoice.balance_due,
        client_name: invoice.customer_name,
        created_at: invoice.created_at,
        service_date: invoice.service_date,
        due_date: invoice.due_date,
      },
    });
    const filename =
      invoice.filename ||
      safeInvoiceFilename(invoice.invoice_number, invoice.customer_name);
    return { blob, filename };
  };

  // Native share sheet — primary on phones ("Send"), secondary on desktop
  // ("Share"). Fast path fire-and-forget to preserve the iOS gesture frame.
  const handleShare = async () => {
    if (pdfBlob && pdfFilename) {
      shareOrDownloadPdf(pdfBlob, pdfFilename);
      return;
    }
    if (!invoice || busy) return;
    setBusy(true);
    try {
      const { blob, filename } = await ensureBlob();
      await shareOrDownloadPdf(blob, filename);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  // Direct download — never opens the share sheet. Primary on laptop/desktop.
  const handleDownloadOnly = async () => {
    if (pdfBlob && pdfFilename) {
      triggerBlobDownload(pdfBlob, pdfFilename);
      return;
    }
    if (!invoice || busy) return;
    setBusy(true);
    try {
      const { blob, filename } = await ensureBlob();
      triggerBlobDownload(blob, filename);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const mobileSend = isMobile && canShare;
  const showShareButton = !mobileSend && canShare;
  const primaryLabel = busy || showGenerating
    ? "Generating…"
    : mobileSend
      ? "Send"
      : "PDF";

  return (
    <PageShell testId="invoice-detail-page" padding="22px 22px 28px" maxWidth={680}>
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 22,
        }}
      >
        <BackLink to="/invoices" label="Invoices" testId="invoice-detail-back" />
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <AuthChip />
          {invoice && (
            <div
              style={{
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: 11,
                letterSpacing: "0.18em",
                color: "#5c584f",
                textTransform: "uppercase",
              }}
            >
              INVOICE #{invoice.invoice_number || "—"}
            </div>
          )}
        </div>
      </header>

      {error && (
        <div
          data-testid="invoice-detail-error"
          role="alert"
          style={{
            padding: "12px 14px",
            borderRadius: 12,
            border: `1px solid ${COLORS.red}`,
            backgroundColor: "rgba(229,75,60,0.08)",
            color: "#ffb3ab",
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 12,
            lineHeight: 1.5,
            marginBottom: 14,
          }}
        >
          {error}
        </div>
      )}

      {!invoice && !error && (
        <div
          data-testid="invoice-detail-loading"
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 12,
            color: COLORS.faint,
            textAlign: "center",
            paddingTop: 40,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
          }}
        >
          // loading
        </div>
      )}

      {invoice && (
        <>
          {/* Hero summary */}
          <section
            data-testid="invoice-detail-hero"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              marginBottom: 18,
            }}
          >
            <div
              style={{
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: 10,
                letterSpacing: "0.2em",
                color: "#5c584f",
                textTransform: "uppercase",
              }}
            >
              Bill to
            </div>
            <div
              style={{
                fontFamily: "'Instrument Serif', Georgia, serif",
                fontStyle: "italic",
                fontSize: 28,
                lineHeight: 1.2,
              }}
            >
              {invoice.customer?.name || "—"}
            </div>
            <div
              style={{
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: 11,
                letterSpacing: "0.05em",
                color: "#8a8472",
                marginTop: 2,
              }}
            >
              {fmtDate(invoice.created_at)}
              {invoice.job?.description ? ` · ${invoice.job.description}` : ""}
            </div>
            <div
              style={{
                marginTop: 14,
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                borderTop: `1px solid ${COLORS.rule || "#3a362e"}`,
                paddingTop: 14,
              }}
            >
              <span
                style={{
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  fontSize: 10,
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  color: "#a8a29a",
                }}
              >
                {typeof invoice.balance_due === "number" &&
                invoice.balance_due > 0 &&
                invoice.balance_due !== invoice.total_due
                  ? "Balance Due"
                  : "Total Due"}
              </span>
              <span
                data-testid="invoice-detail-total"
                style={{
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  fontSize: 30,
                  fontWeight: 600,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {fmtMoney(
                  typeof invoice.balance_due === "number" && invoice.balance_due > 0
                    ? invoice.balance_due
                    : invoice.total_due,
                )}
              </span>
            </div>
          </section>

          {/* Line-item recap from final_extraction */}
          <Recap invoice={invoice} />

          {/* Action row: paid toggle + download (+ share on desktop) */}
          <div
            style={{
              display: "flex",
              gap: 10,
              marginTop: 22,
              flexWrap: "wrap",
            }}
          >
            <button
              data-testid="invoice-detail-paid-toggle"
              onClick={onTogglePaid}
              disabled={busy}
              style={{
                flex: 1,
                padding: "14px 18px",
                borderRadius: 999,
                border: `1px solid ${invoice.paid ? "#3a4d35" : COLORS.rule || "#3a362e"}`,
                backgroundColor: invoice.paid ? "rgba(111,201,111,0.10)" : "transparent",
                color: invoice.paid ? "#9ed395" : "#f4f1ea",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 700,
                fontSize: 12,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                cursor: busy ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                opacity: busy ? 0.6 : 1,
              }}
            >
              {invoice.paid ? (
                <>
                  <Check size={14} /> Paid
                </>
              ) : (
                "Mark Paid"
              )}
            </button>
            <button
              data-testid="invoice-detail-download"
              onClick={mobileSend ? handleShare : handleDownloadOnly}
              disabled={busy}
              style={{
                flex: 1,
                minWidth: 120,
                padding: "14px 18px",
                borderRadius: 999,
                backgroundColor: COLORS.yellow,
                color: "#0f0e0c",
                border: "none",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 800,
                fontSize: 12,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                cursor: busy ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                opacity: busy ? 0.6 : 1,
              }}
            >
              <Download size={14} /> {primaryLabel}
            </button>
            {showShareButton && (
              <button
                data-testid="invoice-detail-share"
                onClick={handleShare}
                disabled={busy}
                style={{
                  flex: 1,
                  minWidth: 120,
                  padding: "14px 18px",
                  borderRadius: 999,
                  border: `1px solid ${COLORS.rule || "#3a362e"}`,
                  backgroundColor: "transparent",
                  color: "#f4f1ea",
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 700,
                  fontSize: 12,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  cursor: busy ? "not-allowed" : "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  opacity: busy ? 0.6 : 1,
                }}
              >
                Share
              </button>
            )}
          </div>

          {/* Reproduction note — this is where a re-download of a previously
              sent invoice can look different from the original. Set the
              expectation honestly: the figures are locked, the layout is
              re-rendered in the current format. (See deferred decision #16.) */}
          <div
            data-testid="invoice-detail-regen-note"
            style={{
              marginTop: 12,
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 10,
              lineHeight: 1.6,
              letterSpacing: "0.04em",
              color: "#5c584f",
            }}
          >
            // amounts are locked; the PDF is re-generated from your saved data,
            so the layout may differ slightly from the original you sent.
          </div>

          {/* Delete row */}
          <div style={{ marginTop: 26 }}>
            {!confirmingDelete ? (
              <button
                data-testid="invoice-detail-delete"
                onClick={() => setConfirmingDelete(true)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#a8a29a",
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  fontSize: 11,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: 0,
                }}
              >
                <Trash2 size={12} /> Delete invoice
              </button>
            ) : (
              <div
                data-testid="invoice-detail-delete-confirm"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                    fontSize: 11,
                    color: "#ffb3ab",
                    letterSpacing: "0.04em",
                  }}
                >
                  Delete this invoice?
                </span>
                <button
                  data-testid="invoice-detail-delete-confirm-yes"
                  onClick={onDelete}
                  disabled={busy}
                  style={{
                    background: "transparent",
                    border: `1px solid ${COLORS.red}`,
                    color: "#ffb3ab",
                    padding: "6px 12px",
                    borderRadius: 999,
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 700,
                    fontSize: 11,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    cursor: busy ? "not-allowed" : "pointer",
                    opacity: busy ? 0.6 : 1,
                  }}
                >
                  Yes, delete
                </button>
                <button
                  onClick={() => setConfirmingDelete(false)}
                  disabled={busy}
                  style={{
                    background: "transparent",
                    border: `1px solid ${COLORS.rule || "#3a362e"}`,
                    color: "#a8a29a",
                    padding: "6px 12px",
                    borderRadius: 999,
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 600,
                    fontSize: 11,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    cursor: busy ? "not-allowed" : "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </>
      )}
      <AppFooter />
    </PageShell>
  );
}

// ---------------------------------------------------------------------------
// Recap — mirrors the PDF layout in the dark theme. BILL TO / JOB SITE
// bands above the line-items table, a flat line-items list (no category
// bands) with notes on a styled second line, totals area with optional
// discount / deposit / BALANCE DUE.
// ---------------------------------------------------------------------------

const _SORTED_LINES = (items) =>
  Array.isArray(items)
    ? [...items].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    : [];

// Parse an ISO "YYYY-MM-DD" as a LOCAL date (avoid the UTC-midnight shift) and
// format short. Mirrors Success.jsx _previewFmtDate.
// Date-only-aware formatter (utils/dateFormat.js) — shared, tested, matches
// the PDF. Pure "YYYY-MM-DD" → local midnight; timestamps → instant.
const _detailFmtDate = (iso) => formatInvoiceDate(iso, { month: "short" });

// "Due date: <date>" — the stored due_date already reflects the terms, so we
// just show the date (no "Net N" suffix). Matches the PDF.
const _detailDueBy = (dueDate) => {
  const dueStr = _detailFmtDate(dueDate);
  return dueStr ? `Due date: ${dueStr}` : "";
};

function Recap({ invoice }) {
  const customer = invoice.customer || {};
  const job = invoice.job || {};
  // Discounts (negative lines) show in the totals area, not the items list.
  const lines = _SORTED_LINES(invoice.line_items).filter(
    (it) => Number(it.amount) >= 0,
  );

  // Business header / payment / notes come from the profile snapshot captured
  // at finalize (logo + identity + payment methods + default terms) plus the
  // invoice-level note + terms overrides — same fields the PDF renders.
  const snap = invoice.profile_snapshot || {};
  const logo = snap.logo;
  const logoSrc =
    logo && logo.data
      ? `data:${logo.mime_type || "image/png"};base64,${logo.data}`
      : null;
  const businessName = snap.business_name || "";
  const tagline = snap.tagline || "";
  const contractorName = snap.contractor_name || "";
  const contactParts = [snap.phone, snap.email].filter(Boolean);
  const address = snap.address || "";
  const license = snap.license_number || "";
  const paymentMethods = (snap.payment_methods || "").toString().trim();
  const dueBy = _detailDueBy(invoice.due_date);
  const issuedDate = _detailFmtDate(invoice.created_at);
  const serviceDateStr =
    invoice.service_date && _detailFmtDate(invoice.service_date) !== issuedDate
      ? _detailFmtDate(invoice.service_date)
      : "";
  // Tri-state notes: explicit "" suppresses; null/undefined inherits the
  // profile footer; a string overrides. Matches the PDF + Success preview.
  const footerNotes =
    invoice.customer_visible_notes !== null &&
    invoice.customer_visible_notes !== undefined
      ? String(invoice.customer_visible_notes)
      : snap.invoice_footer_notes || "";
  const showBusinessHeader =
    !!(logoSrc || businessName || contractorName || address || license) ||
    contactParts.length > 0;

  const subtotal = Number(invoice.subtotal ?? 0);
  const discountTotal = Number(invoice.discount_total ?? 0);
  const hasDiscount = Number.isFinite(discountTotal) && discountTotal !== 0;
  // subtotal is stored net of the discount; show the gross items sum so the
  // DISCOUNT row reads correctly (gross + discount = net).
  const grossSubtotal = +(subtotal - discountTotal).toFixed(2);
  // tax block may live nested under invoice.tax (new shape) or flat as
  // invoice.tax_amount/tax_label (no-op default).
  const taxBlock = invoice.tax || {};
  const taxAmount = Number(taxBlock.amount ?? invoice.tax_amount ?? 0);
  const hasTax = Number.isFinite(taxAmount) && taxAmount > 0;
  const taxLabel = taxBlock.label || invoice.tax_label || "Tax";
  const totalDue = Number(invoice.total_due ?? subtotal + taxAmount);
  const depositAmount = Number(invoice.deposit?.amount ?? 0);
  const hasDeposit = Number.isFinite(depositAmount) && depositAmount > 0;
  const balanceDue = Number(
    invoice.balance_due ?? totalDue - (hasDeposit ? depositAmount : 0),
  );

  return (
    <section
      data-testid="invoice-detail-recap"
      style={{
        marginTop: 22,
        backgroundColor: COLORS.panel,
        border: `1px solid ${COLORS.rule || "#3a362e"}`,
        borderRadius: 14,
        padding: "16px 16px 18px",
      }}
    >
      {/* Business header — logo + identity (mirrors the PDF header band) */}
      {showBusinessHeader && (
        <div
          data-testid="invoice-detail-business-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
            paddingBottom: 14,
            borderBottom: "1px solid #2a2824",
            marginBottom: 14,
          }}
        >
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start", minWidth: 0 }}>
            {logoSrc && (
              <img
                src={logoSrc}
                alt="Business logo"
                data-testid="invoice-detail-logo"
                style={{
                  width: 48,
                  height: 48,
                  objectFit: "contain",
                  borderRadius: 6,
                  backgroundColor: "#f4f1ea",
                  flex: "0 0 auto",
                }}
              />
            )}
            <div style={{ minWidth: 0 }}>
              {businessName && (
                <div
                  style={{
                    fontFamily: "'Instrument Serif', Georgia, serif",
                    fontStyle: "italic",
                    fontSize: 20,
                    lineHeight: 1.1,
                    color: "#f4f1ea",
                  }}
                >
                  {businessName}
                </div>
              )}
              {tagline && (
                <div style={{ fontSize: 11, color: "#8a8472", fontStyle: "italic", marginTop: 2 }}>
                  {tagline}
                </div>
              )}
              {contractorName && (
                <div style={{ fontSize: 12, color: "#f4f1ea", marginTop: 4 }}>{contractorName}</div>
              )}
              {contactParts.length > 0 && (
                <div style={{ fontSize: 11, color: "#8a8472", marginTop: 2 }}>
                  {contactParts.join("  ·  ")}
                </div>
              )}
              {address && (
                <div style={{ fontSize: 11, color: "#8a8472", marginTop: 2 }}>{address}</div>
              )}
              {license && (
                <div style={{ fontSize: 11, color: "#8a8472", marginTop: 2 }}>Lic. #{license}</div>
              )}
            </div>
          </div>
          <div
            style={{
              textAlign: "right",
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              flex: "0 0 auto",
            }}
          >
            {invoice.invoice_number && (
              <>
                <div style={{ fontSize: 9, letterSpacing: "0.2em", color: "#5c584f", textTransform: "uppercase" }}>
                  Invoice
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#f4f1ea", marginTop: 2 }}>
                  #{invoice.invoice_number}
                </div>
              </>
            )}
            <div style={{ fontSize: 10, color: "#8a8472", marginTop: 2 }}>
              {issuedDate}
            </div>
            {serviceDateStr && (
              <div
                data-testid="invoice-detail-service-date"
                style={{ fontSize: 10, color: "#8a8472", marginTop: 2 }}
              >
                Service {serviceDateStr}
              </div>
            )}
            {dueBy && (
              <div data-testid="invoice-detail-due-by" style={{ fontSize: 10, color: "#8a8472", marginTop: 2 }}>
                {dueBy}
              </div>
            )}
          </div>
        </div>
      )}

      {/* BILL TO / JOB SITE bands */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 18,
          paddingBottom: 14,
          borderBottom: "1px solid #2a2824",
          marginBottom: 14,
        }}
      >
        <BandColumn
          label="BILL TO"
          lines={[
            customer.name && { text: customer.name, bold: true, size: 14 },
            customer.company && { text: customer.company },
            customer.address && { text: customer.address },
            customer.phone && { text: customer.phone },
            customer.email && { text: customer.email },
          ]}
        />
        <BandColumn
          label="JOB SITE"
          lines={
            job.site_address
              ? [
                  { text: job.site_address },
                  job.description && { text: job.description, italic: true, color: "#8a8472" },
                ]
              : [
                  { text: "Same as billing", italic: true, color: "#8a8472" },
                  job.description && { text: job.description, italic: true, color: "#8a8472" },
                ]
          }
        />
      </div>

      {/* Line items */}
      <div
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 10,
          letterSpacing: "0.2em",
          color: "#8a8472",
          textTransform: "uppercase",
          marginBottom: 8,
        }}
      >
        Line items
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          {lines.length === 0 && (
            <tr>
              <td
                colSpan={2}
                style={{
                  padding: "10px 0",
                  fontStyle: "italic",
                  fontSize: 12,
                  color: "#5c584f",
                }}
              >
                No line items recorded.
              </td>
            </tr>
          )}
          {(() => {
            const rendered = [];
            lines.forEach((it, i) => {
              rendered.push(<LineRow key={it.id || i} item={it} />);
              if (it.notes) {
                rendered.push(<NotesRow key={`n${i}`} text={it.notes} />);
              }
            });
            return rendered;
          })()}
          <tr>
            <td colSpan={2} style={{ paddingTop: 12 }} />
          </tr>
          <SubRow label="Subtotal" amount={fmtMoney(grossSubtotal)} />
          {hasDiscount && (
            <SubRow
              label="Discount"
              amount={fmtMoney(discountTotal)}
              tone="discount"
            />
          )}
          {hasTax && (
            <SubRow label={taxLabel} amount={fmtMoney(taxAmount)} />
          )}
          {hasDeposit && (
            <SubRow
              label={
                invoice.deposit?.method && String(invoice.deposit.method).trim()
                  ? `Deposit received (${String(invoice.deposit.method).trim()})`
                  : "Deposit received"
              }
              amount={fmtMoney(-depositAmount)}
              tone="discount"
            />
          )}
          <BalanceRow label="Balance Due" amount={fmtMoney(balanceDue)} />
        </tbody>
      </table>

      {paymentMethods && (
        <div
          data-testid="invoice-detail-payment"
          style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid #2a2824" }}
        >
          <div
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 9,
              letterSpacing: "0.2em",
              color: "#8a8472",
              textTransform: "uppercase",
              marginBottom: 4,
            }}
          >
            Payment
          </div>
          <div style={{ fontSize: 12, color: "#a8a29a", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
            {paymentMethods}
          </div>
        </div>
      )}

      {footerNotes && (
        <div
          data-testid="invoice-detail-notes"
          style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid #2a2824" }}
        >
          <div
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 9,
              letterSpacing: "0.2em",
              color: "#8a8472",
              textTransform: "uppercase",
              marginBottom: 4,
            }}
          >
            Notes
          </div>
          <div
            style={{
              fontSize: 12,
              color: "#a8a29a",
              fontStyle: "italic",
              lineHeight: 1.5,
              whiteSpace: "pre-wrap",
            }}
          >
            {footerNotes}
          </div>
        </div>
      )}
    </section>
  );
}

function BandColumn({ label, lines }) {
  const filtered = (lines || []).filter(Boolean);
  return (
    <div>
      <div
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 9,
          letterSpacing: "0.2em",
          color: "#8a8472",
          textTransform: "uppercase",
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      {filtered.length === 0 ? (
        <div style={{ fontSize: 12, color: "#5c584f", fontStyle: "italic" }}>—</div>
      ) : (
        filtered.map((ln, i) => (
          <div
            key={i}
            style={{
              fontSize: ln.size || 12,
              fontWeight: ln.bold ? 600 : 400,
              fontStyle: ln.italic ? "italic" : "normal",
              color: ln.color || "#f4f1ea",
              marginBottom: 2,
              lineHeight: 1.35,
              // Long emails/addresses are single unbreakable tokens — wrap them
              // so they don't push the 2-col band wider → horizontal scroll.
              overflowWrap: "anywhere",
            }}
          >
            {ln.text}
          </div>
        ))
      )}
    </div>
  );
}

function LineRow({ item }) {
  const isNegative = Number(item.amount) < 0;
  const qtyDisplay =
    item.quantity != null
      ? item.unit
        ? `${item.quantity} ${item.unit}`
        : String(item.quantity)
      : "";
  const rateDisplay =
    item.unit_price != null && item.unit_price !== ""
      ? `${fmtMoney(item.unit_price)}/${item.unit || "ea"}`
      : "";
  const detailParts = [qtyDisplay, rateDisplay].filter(Boolean);

  return (
    <tr>
      <td style={{ padding: "8px 0 4px", borderBottom: item.notes ? "none" : "1px solid #2a2824" }}>
        <div style={{ fontSize: 13, color: "#f4f1ea" }}>
          {item.description || "—"}
        </div>
        {detailParts.length > 0 && (
          <div
            style={{
              fontSize: 11,
              color: "#8a8472",
              marginTop: 2,
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            }}
          >
            {detailParts.join(" · ")}
          </div>
        )}
      </td>
      <td
        style={{
          padding: "8px 0 4px",
          borderBottom: item.notes ? "none" : "1px solid #2a2824",
          textAlign: "right",
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 13,
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
          verticalAlign: "top",
          color: isNegative ? "#e8634a" : "#f4f1ea",
        }}
      >
        {fmtMoney(item.amount)}
      </td>
    </tr>
  );
}

function NotesRow({ text }) {
  return (
    <tr>
      <td
        colSpan={2}
        style={{
          padding: "0 0 8px",
          borderBottom: "1px solid #2a2824",
          fontFamily: "'Inter', sans-serif",
          fontSize: 11,
          color: "#8a8472",
          fontStyle: "italic",
        }}
      >
        {text}
      </td>
    </tr>
  );
}

function SubRow({ label, amount, tone }) {
  const isDiscount = tone === "discount";
  const isMuted = tone === "muted";
  return (
    <tr>
      <td
        style={{
          padding: "6px 0 0",
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 10,
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          color: isMuted ? "#5c584f" : "#a8a29a",
        }}
      >
        {label}
      </td>
      <td
        style={{
          padding: "6px 0 0",
          textAlign: "right",
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 13,
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
          color: isDiscount ? "#e8634a" : isMuted ? "#8a8472" : "#f4f1ea",
        }}
      >
        {amount}
      </td>
    </tr>
  );
}

function BalanceRow({ label, amount }) {
  return (
    <tr>
      <td
        style={{
          padding: "16px 0 0",
          borderTop: "2px solid #e8dc2d",
          marginTop: 14,
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 11,
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          color: "#a8a29a",
        }}
      >
        {label}
      </td>
      <td
        style={{
          padding: "16px 0 0",
          borderTop: "2px solid #e8dc2d",
          textAlign: "right",
          fontFamily: "'Instrument Serif', Georgia, serif",
          fontSize: 24,
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
          color: "#e8dc2d",
        }}
      >
        {amount}
      </td>
    </tr>
  );
}
